import { useQuery } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";

import type { OutlookProjectConfigurationDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormInput } from "@/components/Form/FormInput";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { createRequiredStringRule } from "@/helpers/rules";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { integrationMutations } from "@/queries/integrations/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export type OutlookFormValues = OutlookProjectConfigurationDto;

export function OutlookPage(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();
  const integrationQueries = useIntegrationQueries();

  const { data, isFetching, error } = useQuery(integrationQueries.outlookConfiguration());
  const mutation = integrationMutations.useEditOutlookConfiguration();

  if (!hasPermission((x) => x.integrations.canManageOutlook)) {
    return <ErrorPage status={404} />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isFetching) {
    return <FullSizeLoader withPadding />;
  }

  const name = t("page.integration.outlook.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={name}
      breadcrumbs={
        <Breadcrumbs
          pages={[{ name: t("page.integration.title"), to: routes.projects.integrations({ slug }) }, { name }]}
        />
      }
    >
      <OutlookForm
        defaultValues={{
          enabled: data?.enabled || false,
          tenantId: data?.tenantId || "",
          clientId: data?.clientId || "",
          clientSecret: data?.clientSecret || "",
          baseUrl: data?.baseUrl || "",
        }}
        isSubmitting={mutation.isPending}
        onSubmit={mutation.mutate}
      />
    </DocumentPage>
  );
}

function OutlookForm({
  defaultValues,
  isSubmitting,
  onSubmit,
}: {
  defaultValues: Partial<OutlookFormValues>;
  isSubmitting: boolean;
  onSubmit: (data: OutlookProjectConfigurationDto) => void;
}): React.ReactNode {
  const { t } = useTranslation();
  const formMethods = useForm<OutlookFormValues>({ defaultValues });
  const currEnabled = useWatch({ control: formMethods.control, name: "enabled" });

  return (
    <Form.Root formMethods={formMethods} onSubmit={onSubmit}>
      <Form.Container title={t("page.integration.outlook.title")}>
        <Form.Body>
          <FormCheckbox<OutlookFormValues, "enabled">
            id="enabled"
            name="enabled"
            label={t("common.action.enable")}
            onChange={(e) => {
              if (currEnabled && !e.target.checked) {
                formMethods.clearErrors();
              }
            }}
          />

          <FormField label={t("page.integration.outlook.form.tenant-id")} required>
            <FormInput<OutlookFormValues, "tenantId">
              id="tenantId"
              name="tenantId"
              rules={{
                validate: {
                  required: currEnabled
                    ? createRequiredStringRule(t, "page.integration.outlook.form.tenant-id")
                    : () => undefined,
                },
              }}
              disabled={!currEnabled}
            />
          </FormField>

          <FormField label={t("page.integration.outlook.form.client-id")} required>
            <FormInput<OutlookFormValues, "clientId">
              id="clientId"
              name="clientId"
              rules={{
                validate: {
                  required: currEnabled
                    ? createRequiredStringRule(t, "page.integration.outlook.form.client-id")
                    : () => undefined,
                },
              }}
              disabled={!currEnabled}
            />
          </FormField>

          <FormField label={t("page.integration.outlook.form.client-secret")} required>
            <FormInput<OutlookFormValues, "clientSecret">
              id="clientSecret"
              name="clientSecret"
              type="password"
              rules={{
                validate: {
                  required: currEnabled
                    ? createRequiredStringRule(t, "page.integration.outlook.form.client-secret")
                    : () => undefined,
                },
              }}
              disabled={!currEnabled}
            />
          </FormField>

          <FormField label={t("page.integration.outlook.form.base-url")}>
            <FormInput<OutlookFormValues, "baseUrl"> id="baseUrl" name="baseUrl" disabled={!currEnabled} />
          </FormField>
        </Form.Body>

        <Form.Footer
          renderPrimaryAction={() => (
            <Button type="submit" isLoading={isSubmitting}>
              {t("common.action.save")}
            </Button>
          )}
        />
      </Form.Container>
    </Form.Root>
  );
}
